import { create } from "zustand"
import { createModelsApi } from "@/lib/api/models"
import type { ModelCatalogEntry } from "@/lib/types/model-catalog"
import type { ModelCategory } from "@/lib/types/module"

interface ModelCatalogStore {
  models: ModelCatalogEntry[]
  status: "idle" | "loading" | "ready" | "error"

  fetch: (getToken: () => Promise<string | null>) => Promise<void>
  getByCategory: (category: ModelCategory) => ModelCatalogEntry[]
  getGroupedByProvider: (categories: ModelCategory[]) => Record<string, ModelCatalogEntry[]>
  getById: (id: string) => ModelCatalogEntry | undefined
  getDefaultForModule: (moduleId: string) => ModelCatalogEntry | undefined
}

export const useModelCatalogStore = create<ModelCatalogStore>((set, get) => ({
  models: [],
  status: "idle",

  fetch: async (getToken) => {
    if (get().status === "ready" || get().status === "loading") return
    set({ status: "loading" })
    try {
      const api = createModelsApi({ getToken })
      const models = await api.list()
      set({ models, status: "ready" })
    } catch {
      set({ status: "error" })
    }
  },

  getByCategory: (category) =>
    get().models.filter((m) => m.categories.includes(category)),

  getGroupedByProvider: (categories) => {
    const result: Record<string, ModelCatalogEntry[]> = {}
    for (const model of get().models) {
      if (!model.categories.some((c) => categories.includes(c as ModelCategory))) continue
      if (!result[model.provider_label]) result[model.provider_label] = []
      result[model.provider_label].push(model)
    }
    return result
  },

  getById: (id) => get().models.find((m) => m.id === id),

  getDefaultForModule: (moduleId) =>
    get().models.find((m) => m.default_for_modules.includes(moduleId)),
}))
